import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff, MdLogin, MdLockReset, MdContactMail } from 'react-icons/md';
import ContactUsModal from '../components/ContactUsModal';

// ─────────────────────────────────────────────────────────────────────────────
// HCEN · Hospital Login (Cognito · May 22, 2026 · Phase A)
//   • Plain email + password sign-in.
//   • If the Lambda answers with `challenge: NEW_PASSWORD_REQUIRED` we render
//     the set-password form inline (no extra route) and complete the login.
//   • Forgot-password link routes to /forgot-password.
// ─────────────────────────────────────────────────────────────────────────────
export default function Login() {
  const { login, respondPasswordChallenge } = useApp();
  const navigate = useNavigate();

  const [form, setForm]               = useState({ email:'', password:'' });
  const [showPwd, setShowPwd]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [agreed, setAgreed]           = useState(false);
  const [agreeError, setAgreeError]   = useState(false);

  // Challenge state (NEW_PASSWORD_REQUIRED)
  const [challenge, setChallenge]     = useState(null);   // { session, email }
  const [newPwd, setNewPwd]           = useState('');
  const [newPwd2, setNewPwd2]         = useState('');
  const [contactOpen, setContactOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!agreed) { setAgreeError(true); return; }
    setAgreeError(false);
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.success) {
      navigate('/dashboard');
      return;
    }
    if (res.challenge === 'NEW_PASSWORD_REQUIRED') {
      setChallenge({ session: res.session, email: res.email || form.email });
      setError('');
      return;
    }
    setError(res.error);
  };

  const submitChallenge = async (e) => {
    e.preventDefault();
    setError('');
    if (newPwd.length < 8)           return setError('Password must be at least 8 characters.');
    if (!/[A-Z]/.test(newPwd))       return setError('Password must contain an uppercase letter.');
    if (!/[a-z]/.test(newPwd))       return setError('Password must contain a lowercase letter.');
    if (!/[0-9]/.test(newPwd))       return setError('Password must contain a number.');
    if (!/[!@#$%^&*?\-_]/.test(newPwd)) return setError('Password must contain a symbol (!@#$%^&*?-_)');
    if (newPwd !== newPwd2)          return setError('Passwords do not match.');
    setLoading(true);
    const res = await respondPasswordChallenge(challenge.email, newPwd, challenge.session);
    setLoading(false);
    if (res.success) navigate('/dashboard');
    else setError(res.error || 'Could not set password.');
  };

  return (
    <div className="auth-page">
      <div className="auth-page__glow-teal" />
      <div className="auth-page__glow-blue" />

      <div style={{ width:'100%', maxWidth:440, position:'relative', zIndex:1 }}>

        {/* Branding */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <img src="/hcen_logo.png" alt="HCEN"
            style={{ width:80, height:80, borderRadius:22, objectFit:'cover', marginBottom:14, boxShadow:'0 8px 32px rgba(0,212,170,0.25)' }} />
          <h1 style={{ fontFamily:'Outfit,sans-serif', fontSize:34, fontWeight:800, background:'linear-gradient(135deg,var(--text-primary),var(--color-primary))', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', marginBottom:4 }}>HCEN</h1>
          <p style={{ color:'var(--text-secondary)', fontSize:15 }}>Healthcare Emergency Network · Hospital Dashboard</p>
        </div>

        <div className="glass-card" style={{ padding:32 }}>
          {!challenge ? (
            <>
              <h2 style={{ fontFamily:'Outfit,sans-serif', fontSize:22, fontWeight:700, marginBottom:22 }}>Welcome Back</h2>

              <form onSubmit={handleSubmit}>
                {/* Email */}
                <div className="form-group">
                  <label className="form-label" htmlFor="login-email">Email address</label>
                  <div className="auth-input-wrap">
                    <MdEmail className="auth-input-icon" aria-hidden />
                    <input id="login-email" type="email" className="form-input" placeholder="hospital@hcen.com" value={form.email}
                      autoComplete="email"
                      onChange={e => setForm(p => ({...p, email:e.target.value}))} required />
                  </div>
                </div>

                {/* Password */}
                <div className="form-group">
                  <label className="form-label" htmlFor="login-password">Password</label>
                  <div className="auth-input-wrap">
                    <MdLock className="auth-input-icon" aria-hidden />
                    <input id="login-password" type={showPwd ? 'text' : 'password'} className="form-input has-toggle" placeholder="Enter your password" value={form.password}
                      autoComplete="current-password"
                      onChange={e => setForm(p => ({...p, password:e.target.value}))} required />
                    <button type="button" className="auth-input-toggle" onClick={() => setShowPwd(p => !p)}
                      aria-label={showPwd ? 'Hide password' : 'Show password'}>
                      {showPwd ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                  <div style={{ textAlign:'right', marginTop:6 }}>
                    <Link to="/forgot-password" style={{ fontSize:12, color:'var(--color-secondary)', textDecoration:'none', fontWeight:600 }}>
                      Forgot password?
                    </Link>
                  </div>
                </div>

                {/* Terms & Privacy */}
                <div style={{ marginBottom: 18 }}>
                  <label
                    style={{ display:'flex', alignItems:'flex-start', gap:12, cursor:'pointer', userSelect:'none' }}
                    onClick={() => { setAgreed(p => !p); setAgreeError(false); }}
                  >
                    <div style={{
                      width: 22, height: 22, minWidth: 22, borderRadius: 6,
                      border: agreeError ? '2px solid var(--color-danger)' : agreed ? '2px solid var(--color-primary)' : '2px solid var(--border-color-hover)',
                      background: agreed ? 'var(--color-primary-glow)' : 'var(--surface-subtle)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', marginTop: 1,
                    }}>
                      {agreed && (
                        <svg width="13" height="10" viewBox="0 0 13 10" fill="none">
                          <path d="M1 5L5 9L12 1" stroke="var(--color-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <span style={{ fontSize:13, color: agreeError ? 'var(--color-danger)' : 'var(--text-secondary)', lineHeight:1.6 }}>
                      I have read and agree to the{' '}
                      <Link to="/terms"   target="_blank" onClick={e => e.stopPropagation()} style={{ color:'var(--color-secondary)', textDecoration:'underline', fontWeight:600 }}>Terms & Conditions</Link>{' '}and{' '}
                      <Link to="/privacy" target="_blank" onClick={e => e.stopPropagation()} style={{ color:'var(--color-primary)', textDecoration:'underline', fontWeight:600 }}>Privacy Policy</Link>{' '}of HCEN.
                    </span>
                  </label>
                  {agreeError && (
                    <div style={{ marginTop:7, marginLeft:34, fontSize:12, color:'#ef4444' }}>
                      Please accept the Terms & Conditions to continue.
                    </div>
                  )}
                </div>

                {error && (
                  <div style={{ padding:'11px 14px', background:'var(--color-danger-glow)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'var(--radius-md)', color:'var(--color-danger)', fontSize:14, marginBottom:14 }}>
                    {error}
                  </div>
                )}

                <button type="submit" className="btn btn-primary btn-lg"
                  disabled={loading}
                  style={{ width:'100%', justifyContent:'center', opacity: !agreed ? 0.6 : 1, cursor: !agreed ? 'not-allowed' : 'pointer' }}>
                  {loading
                    ? <><span className="anim-spin" style={{ width:16, height:16, border:'2px solid var(--text-muted)', borderTopColor:'#ffffff', borderRadius:'50%', display:'inline-block' }} /> Logging in...</>
                    : <><MdLogin /> Login to Dashboard</>}
                </button>
              </form>

              <div style={{ textAlign:'center', marginTop:22, paddingTop:20, borderTop:'1px solid var(--border-color)' }}>
                <div style={{ fontSize:14, color:'var(--text-muted)', marginBottom:12 }}>New hospital? Join HCEN Network</div>
                <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
                  <Link to="/apply?fresh=1" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 20px', background:'var(--color-primary-glow)', border:'1px solid rgba(0,212,170,0.3)', borderRadius:10, color:'var(--color-primary)', fontWeight:700, fontSize:13, textDecoration:'none' }}>🏥 Apply Now</Link>
                  <Link to="/apply?resume=prompt" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 20px', background:'var(--color-warning-glow)', border:'1px solid rgba(217,119,6,0.3)', borderRadius:10, color:'var(--color-warning)', fontWeight:600, fontSize:13, textDecoration:'none' }}>📋 Resume</Link>
                  <button type="button" onClick={() => setContactOpen(true)}
                    style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 20px', background:'rgba(99,102,241,0.1)', border:'1px solid rgba(99,102,241,0.28)', borderRadius:10, color:'#818cf8', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                    <MdContactMail /> Get in Touch
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <h2 style={{ fontFamily:'Outfit,sans-serif', fontSize:22, fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
                <MdLockReset style={{ color:'var(--color-primary)' }} /> Set Your New Password
              </h2>
              <p style={{ color:'var(--text-secondary)', fontSize:13, marginBottom:22 }}>
                Welcome to HCEN. This is your first sign-in for <b>{challenge.email}</b>. Please choose a new password to continue.
              </p>

              <form onSubmit={submitChallenge}>
                <div className="form-group">
                  <label className="form-label" htmlFor="new-password">New password</label>
                  <div className="auth-input-wrap">
                    <MdLock className="auth-input-icon" aria-hidden />
                    <input id="new-password" type="password" className="form-input" placeholder="At least 8 characters" value={newPwd}
                      autoComplete="new-password"
                      onChange={e => setNewPwd(e.target.value)} required />
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6, lineHeight:1.5 }}>
                    Must be 8+ chars with uppercase, lowercase, a number, and a symbol.
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="confirm-password">Confirm new password</label>
                  <div className="auth-input-wrap">
                    <MdLock className="auth-input-icon" aria-hidden />
                    <input id="confirm-password" type="password" className="form-input" placeholder="Re-enter the same password" value={newPwd2}
                      autoComplete="new-password"
                      onChange={e => setNewPwd2(e.target.value)} required />
                  </div>
                </div>

                {error && (
                  <div style={{ padding:'11px 14px', background:'var(--color-danger-glow)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'var(--radius-md)', color:'var(--color-danger)', fontSize:14, marginBottom:14 }}>
                    {error}
                  </div>
                )}

                <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ width:'100%', justifyContent:'center' }}>
                  {loading ? 'Setting password…' : 'Set Password & Continue'}
                </button>

                <button type="button" onClick={() => { setChallenge(null); setError(''); setNewPwd(''); setNewPwd2(''); }}
                  style={{ width:'100%', marginTop:12, padding:'10px', background:'transparent', border:'none', color:'var(--text-muted)', fontSize:13, cursor:'pointer' }}>
                  ← Cancel
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <ContactUsModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
